import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { filter, map, Observable, take, switchMap, forkJoin, of, timeout, catchError, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as userActions from '../../../shared/src/lib/store/actions/user.actions';
import * as formationActions from '../../../shared/src/lib/store/actions/formation.actions';
import * as groupActions from '../../../shared/src/lib/store/actions/group.actions';
import * as routePermissionActions from '../../../shared/src/lib/store/actions/routePermission.actions';
import { AppStateStudent } from './student.states';
import { Student } from '../../../shared/src/lib/model/student.model';

@Injectable({
    providedIn: 'root'
})
export class StudentInitialDataResolver implements Resolve<any>
{
    /**
     * Constructor
     */
    constructor(
        private store: Store<AppStateStudent>,
        private translateService: TranslateService
    ) {
    }

    waitUser(): Observable<any> {
        return this.store.select("user").pipe(
            map(store => store),
            filter(store => store.loading === false && store.me !== null),
            take(1)
        )
    }

    waitTranslation(): Observable<any> {
        // Vérifier immédiatement si les traductions sont déjà chargées
        const currentLang = this.translateService.currentLang || this.translateService.defaultLang;
        const translations = this.translateService.translations[currentLang];
        
        if (translations && Object.keys(translations).length > 0) {
            // Les traductions sont déjà disponibles, retourner immédiatement
            return of(translations);
        }
        
        // Sinon, attendre le prochain changement de traduction
        return this.translateService.onTranslationChange.pipe(
            filter(() => {
                const lang = this.translateService.currentLang;
                const trans = this.translateService.translations[lang];
                return trans && Object.keys(trans).length > 0;
            }),
            take(1)
        );
    }

    waitFormations(): Observable<any> {
        return this.store.select("formation").pipe(
            map(store => store),
            filter(store => {
                // Attendre que loading soit false ET que formations soit défini (pas null)
                const isReady = store.loading === false && store.formations !== null;
                return isReady;
            }),
            take(1)
        )
    }

    waitGroups(): Observable<any> {
        return this.store.select("group").pipe(
            map(store => store),
            filter(store => {
                const isReady = store.loading === false && store.groups !== null;
                return isReady;
            }),
            take(1)
        )
    }

    waitRoutePermissions(): Observable<any> {
        return this.store.select("routePermission").pipe(
            map(store => store),
            filter(store => {
                const isReady = store.loading === false && store.routePermissions !== null;
                return isReady;
            }),
            take(1)
        )
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Use this resolver to resolve initial mock-api for the application
     * Charge l'utilisateur, ses formations, les groupes et les permissions de routes
     *
     * @param route
     * @param state
     */
    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        console.log('StudentInitialDataResolver: Starting data resolution process.');
        // 1. Charger l'utilisateur
        this.store.dispatch(userActions.GetUser());
        
        return this.waitUser().pipe(
            switchMap((userState) => {
                const student = userState.me as Student;
                
                // 2. Charger en parallèle : formations, groups et routePermissions
                this.store.dispatch(formationActions.LoadFormations({ studentId: student.id }));
                this.store.dispatch(groupActions.LoadGroups());
                this.store.dispatch(routePermissionActions.LoadRoutePermissions());
                
                // 3. Attendre que tout soit chargé en parallèle (formations, groups, routePermissions et traductions)
                return forkJoin([
                    this.waitFormations(),
                    this.waitGroups(),
                    this.waitRoutePermissions(),
                    this.waitTranslation()
                ]).pipe(
                    timeout(10000), // Timeout de 10 secondes
                    catchError((error) => {
                        // Retourner des valeurs par défaut en cas de timeout
                        return of([
                            { formations: [], loading: false },
                            { groups: [], loading: false },
                            { routePermissions: [], loading: false }
                        ]);
                    }),
                    map(([formationState, groupState, routePermissionState]) => {
                        // 4. Sélectionner la formation par défaut si disponible
                        if (formationState.formations.length > 0) {
                            let selectedFormation = null;
                            
                            // Essayer de restaurer la dernière formation sélectionnée depuis localStorage
                            const lastSelectedId = localStorage.getItem('last_selected_formation_id');
                            if (lastSelectedId) {
                                const parsedId = parseInt(lastSelectedId, 10);
                                selectedFormation = formationState.formations.find(f => f.id === parsedId);
                            }
                            
                            // Si la formation mémorisée n'existe plus, utiliser la logique par défaut
                            if (!selectedFormation) {
                                // Prioriser les formations actives
                                const activeFormations = formationState.formations.filter(f => f.status === 'active');
                                selectedFormation = activeFormations.length > 0 
                                    ? activeFormations[0] 
                                    : formationState.formations[0];
                            }
                            
                            this.store.dispatch(formationActions.SelectFormation({ formation: selectedFormation }));
                        }
                        
                        return {
                            user: student,
                            formations: formationState.formations,
                            groups: groupState.groups,
                            routePermissions: routePermissionState.routePermissions
                        };
                    })
                );
            })
        );
    }
}
